import React, { useState } from 'react';
import Layout from '@/components/Layout';
import PageMeta from '@/components/seo/PageMeta';
import JsonLd from '@/components/seo/JsonLd';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface GlossaryTerm {
  term: string;
  definition: string;
  relatedTerms?: string[];
}

const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    term: "AI Governance",
    definition: "The framework of policies, procedures, and oversight mechanisms that guide the development, deployment, and monitoring of artificial intelligence systems within an organization. AI governance ensures AI is used responsibly, ethically, and in compliance with regulations while maximizing strategic value.",
    relatedTerms: ["AI Ethics", "AI Risk Management", "Board Oversight"]
  },
  {
    term: "Algorithmic Bias",
    definition: "Systematic errors in AI systems that create unfair outcomes for certain groups based on factors like race, gender, age, or socioeconomic status. Bias can occur from training data, algorithm design, or deployment context, and boards must ensure processes exist to detect and mitigate such biases.",
    relatedTerms: ["AI Ethics", "Fairness", "Explainable AI"]
  },
  {
    term: "AI Risk Management",
    definition: "The systematic process of identifying, assessing, mitigating, and monitoring risks associated with AI systems. This includes technical risks (model failures, security vulnerabilities), operational risks (integration issues), regulatory risks (compliance), and strategic risks (competitive positioning).",
    relatedTerms: ["AI Governance", "Risk Assessment", "Due Diligence"]
  },
  {
    term: "Board Fiduciary Duty",
    definition: "The legal obligation of board directors to act in the best interests of the organization and its stakeholders. In the context of AI, this includes ensuring appropriate oversight of AI risks, strategic AI investments, and compliance with AI regulations.",
    relatedTerms: ["Corporate Governance", "AI Oversight", "Due Diligence"]
  },
  {
    term: "EU AI Act",
    definition: "The European Union's comprehensive regulation on artificial intelligence, establishing a risk-based framework for AI governance. It classifies AI systems into risk categories (unacceptable, high, limited, minimal) and imposes requirements on developers and deployers, with significant penalties for non-compliance.",
    relatedTerms: ["AI Regulation", "Compliance", "High-Risk AI"]
  },
  {
    term: "Explainable AI (XAI)",
    definition: "Artificial intelligence systems designed to provide clear, understandable explanations for their decisions and predictions. XAI is crucial for regulatory compliance, user trust, bias detection, and effective human oversight of AI systems.",
    relatedTerms: ["Transparency", "Interpretability", "AI Governance"]
  },
  {
    term: "Generative AI",
    definition: "AI systems that can create new content including text, images, code, audio, and video. Examples include large language models (like GPT) and image generators. Generative AI presents unique governance challenges around intellectual property, misinformation, and quality control.",
    relatedTerms: ["Large Language Models", "AI Content", "Deep Learning"]
  },
  {
    term: "High-Risk AI",
    definition: "Under the EU AI Act, AI systems used in areas that pose significant risks to health, safety, or fundamental rights. These include AI for critical infrastructure, education, employment, essential services, law enforcement, and biometric identification. High-risk AI requires conformity assessments and ongoing monitoring.",
    relatedTerms: ["EU AI Act", "Risk Classification", "Compliance"]
  },
  {
    term: "AI Ethics",
    definition: "The moral principles and values that guide the development and use of AI systems. Key ethical considerations include fairness, transparency, accountability, privacy, safety, and human autonomy. Boards must ensure ethical guidelines are embedded in AI governance frameworks.",
    relatedTerms: ["Responsible AI", "AI Governance", "Algorithmic Bias"]
  },
  {
    term: "Machine Learning",
    definition: "A subset of artificial intelligence where systems learn patterns from data to make predictions or decisions without being explicitly programmed for each task. Machine learning is the foundation of most modern AI applications and requires quality data governance.",
    relatedTerms: ["Deep Learning", "Training Data", "AI Models"]
  },
  {
    term: "Model Governance",
    definition: "The policies and processes for managing AI models throughout their lifecycle, including development, validation, deployment, monitoring, and retirement. Effective model governance ensures models perform as expected and remain compliant over time.",
    relatedTerms: ["AI Governance", "Model Risk", "MLOps"]
  },
  {
    term: "AI Audit",
    definition: "An independent assessment of AI systems to evaluate their performance, fairness, security, and compliance with regulations and organizational policies. AI audits help boards ensure accountability and identify areas for improvement.",
    relatedTerms: ["Compliance", "Due Diligence", "AI Governance"]
  },
  {
    term: "Responsible AI",
    definition: "An approach to developing and deploying AI that prioritizes ethics, transparency, fairness, accountability, and societal benefit. Responsible AI frameworks help organizations build trustworthy AI systems that align with stakeholder values.",
    relatedTerms: ["AI Ethics", "AI Governance", "Trustworthy AI"]
  },
  {
    term: "AI Transparency",
    definition: "The practice of making AI systems understandable to stakeholders, including how they work, what data they use, how decisions are made, and what limitations exist. Transparency is a regulatory requirement and essential for stakeholder trust.",
    relatedTerms: ["Explainable AI", "AI Disclosure", "Accountability"]
  },
  {
    term: "Training Data",
    definition: "The dataset used to teach machine learning models patterns and relationships. Training data quality directly impacts AI performance and fairness. Governance of training data includes ensuring accuracy, representativeness, consent, and privacy compliance.",
    relatedTerms: ["Data Governance", "Machine Learning", "Algorithmic Bias"]
  },
  {
    term: "AI Incident Response",
    definition: "The processes and procedures for responding to AI system failures, security breaches, or harmful outputs. Boards should ensure organizations have AI-specific incident response plans, communication protocols, and remediation processes.",
    relatedTerms: ["Crisis Management", "AI Risk", "Cybersecurity"]
  },
  {
    term: "Data Governance",
    definition: "The overall management of data availability, usability, integrity, and security within an organization. For AI, data governance is critical because AI systems depend on quality data for training, validation, and operation.",
    relatedTerms: ["Training Data", "Privacy", "AI Governance"]
  },
  {
    term: "AI Oversight Committee",
    definition: "A board or executive committee responsible for governing AI strategy, risk, and ethics within an organization. The committee establishes policies, monitors AI initiatives, ensures compliance, and reports to the full board on AI matters.",
    relatedTerms: ["Board Governance", "AI Governance", "Corporate Structure"]
  }
];

const GlossaryPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const sortedTerms = [...GLOSSARY_TERMS].sort((a, b) => a.term.localeCompare(b.term));
  
  const filteredTerms = sortedTerms.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Create DefinedTermSet schema for SEO
  const glossarySchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "AI Governance Glossary for Board Directors",
    "description": "Comprehensive glossary of AI governance terminology for board directors and corporate governance professionals.",
    "url": "https://boardsight.ai/glossary",
    "hasDefinedTerm": GLOSSARY_TERMS.map(item => ({
      "@type": "DefinedTerm",
      "name": item.term,
      "description": item.definition
    }))
  };

  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const letter = term.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {} as Record<string, GlossaryTerm[]>);

  return (
    <Layout>
      <PageMeta
        title="AI Governance Glossary for Board Directors"
        description="Comprehensive glossary of AI governance terms, definitions, and concepts essential for board directors. Learn key terminology including AI ethics, EU AI Act, explainable AI, and more."
        canonicalUrl="/glossary"
        keywords="AI governance glossary, AI terms, board directors AI vocabulary, EU AI Act definitions, explainable AI, AI ethics terminology"
      />
      <JsonLd data={glossarySchema} />

      <div className="container max-w-4xl py-12 px-4">
        {/* Hero Section */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            AI Governance Glossary
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Essential terminology for board directors navigating AI oversight and governance responsibilities.
          </p>
        </header>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search terms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Alphabet Navigation */}
        <nav className="flex flex-wrap gap-2 mb-8" aria-label="Alphabet navigation">
          {Object.keys(groupedTerms).sort().map(letter => (
            <a
              key={letter}
              href={`#letter-${letter}`}
              className="w-8 h-8 flex items-center justify-center rounded bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors font-medium"
            >
              {letter}
            </a>
          ))}
        </nav>

        {/* Terms List */}
        <div className="space-y-8">
          {Object.keys(groupedTerms).sort().map(letter => (
            <section key={letter} id={`letter-${letter}`}>
              <h2 className="text-2xl font-bold text-primary mb-4 border-b pb-2">
                {letter}
              </h2>
              <dl className="space-y-6">
                {groupedTerms[letter].map((item, index) => (
                  <div key={index} className="bg-secondary/30 rounded-lg p-6">
                    <dt className="text-xl font-semibold text-foreground mb-2">
                      {item.term}
                    </dt>
                    <dd className="text-muted-foreground leading-relaxed">
                      {item.definition}
                    </dd>
                    {item.relatedTerms && item.relatedTerms.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-sm text-muted-foreground">Related:</span>
                        {item.relatedTerms.map((related, i) => (
                          <span
                            key={i}
                            className="text-sm bg-primary/10 text-primary px-2 py-1 rounded"
                          >
                            {related}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        {filteredTerms.length === 0 && (
          <p className="text-center text-muted-foreground py-12">
            No terms found matching "{searchTerm}"
          </p>
        )}
      </div>
    </Layout>
  );
};

export default GlossaryPage;
